/* eslint-disable no-empty,no-console */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import styled from 'styled-components';
import { inject, observer } from 'mobx-react';

import { KENTICO_TYPES, KENTICO_HTML_MODIFIERS } from 'common/consts/kentico';
import DOM_NODES from 'common/consts/domNodes';

import Header2 from 'elements/Header2';
import Header3 from 'elements/Header3';
import Header4 from 'elements/Header4';
import Header5 from 'elements/Header5';
import Paragraph from 'elements/Paragraph';
import BlogArticleBoldText from 'elements/BlogArticleBoldText';
import Image from 'elements/Image';
import HtmlModifierModel from 'models/HtmlModifier.model';

import DividedBlogSection from 'kentico-blocks/DividedBlogSection';
import BlogImageSlider from 'kentico-blocks/BlogImageSlider';
import ImageLink from 'kentico-blocks/ImageLink';
import root from 'window-or-global';
import { safeHtmlWithBr } from 'common/methods/string';
import UnorderedList from 'elements/UnorderedList';
import OrderedList from 'elements/OrderedList';
import LI from 'elements/LI';
import BlogArticleLink from 'elements/BlogArticleLink';

const BoldText = styled(DOM_NODES.STRONG)`
  font-weight: bold;
`;
const ItalicText = styled(DOM_NODES.I)``;

/* Disable ESlint rules for this file */
/* eslint react/jsx-one-expression-per-line: "off" */
/* eslint react/require-default-props: "off" */
/* eslint react/forbid-prop-types: "off" */
/* eslint react/no-array-index-key: "off" */

export const styleFactory = (htmlModifiers) => {
  const inlineStyles = {};
  if (htmlModifiers) {
    htmlModifiers.forEach((htmlModifier) => {
      switch (htmlModifier.property) {
      case KENTICO_HTML_MODIFIERS.BACKGROUND_COLOR:
        inlineStyles.backgroundColor = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.COLOR:
        inlineStyles.color = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.PADDING_TOP:
        inlineStyles.paddingTop = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.PADDING_RIGHT:
        inlineStyles.paddingRight = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.PADDING_BOTTOM:
        inlineStyles.paddingBottom = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.PADDING_LEFT:
        inlineStyles.paddingLeft = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.MARGIN_TOP:
        inlineStyles.marginTop = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.MARGIN_RIGHT:
        inlineStyles.marginRight = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.MARGIN_BOTTOM:
        inlineStyles.marginBottom = htmlModifier.value; break;
      case KENTICO_HTML_MODIFIERS.MARGIN_LEFT:
        inlineStyles.marginLeft = htmlModifier.value; break;
      default:
      }
    });
  }
  return inlineStyles;
};

@inject('windowStore')
@observer
export default class HTMLParser extends Component {
  windowStore = this.props.windowStore;

  static propTypes = {
    htmlBody: PropTypes.object,
    htmlModifiers: PropTypes.arrayOf(PropTypes.instanceOf(HtmlModifierModel)),
    blogArticle: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    const { htmlModifiers, blogArticle } = props;
    this.modifiedStyles = styleFactory(htmlModifiers);
    this.cameFromBlogArticle = blogArticle;
  }

  parseHeader = (domNode) => {
    if (domNode.name === 'h2') {
      return (
        <Header2>
          {domNode.children[0].data}
        </Header2>
      );
    }

    if (domNode.name === 'h3') {
      return (
        <Header3>
          {domNode.children[0].data}
        </Header3>
      );
    }

    if (domNode.name === 'h4') {
      return (
        <Header4>
          {domNode.children[0].data}
        </Header4>
      );
    }

    if (domNode.name === 'h5') {
      return (
        <Header5>
          {domNode.children[0].data}
        </Header5>
      );
    }
    return null;
  };

  parseList = (domNode) => {
    if (domNode.name === DOM_NODES.UL) {
      return (
        <UnorderedList>
          {domNode.children && domNode.children.map(this.parseFunction)}
        </UnorderedList>
      );
    }

    if (domNode.name === DOM_NODES.OL) {
      return (
        <OrderedList>
          {domNode.children && domNode.children.map(this.parseFunction)}
        </OrderedList>
      );
    }

    return null;
  };

  parseFunction = (domNode) => {
    const header = this.parseHeader(domNode);
    if (header) {
      return header;
    }

    const list = this.parseList(domNode);
    if (list) {
      return list;
    }

    if (domNode.name === DOM_NODES.LI) {
      return (
        <LI>
          {domNode.children && domNode.children.map(this.parseFunction)}
        </LI>
      );
    }

    if (domNode.name === DOM_NODES.A) {
      return (
        <BlogArticleLink {...domNode.attribs}>
          {domNode.children && domNode.children.map(this.parseFunction)}
        </BlogArticleLink>
      );
    }

    if (domNode.name === DOM_NODES.EM) {
      return (
        <ItalicText>
          {domNode.children && domNode.children.map(this.parseFunction)}
        </ItalicText>
      );
    }

    if (domNode.name === DOM_NODES.STRONG) {
      return (
        <BlogArticleBoldText>
          {domNode.children && domNode.children.map(this.parseFunction)}
        </BlogArticleBoldText>
      );
    }

    if (domNode.name === DOM_NODES.DIV) {
      if (!domNode.children.length) return null;

      const model = domNode.children[0].attribs;

      if (model.constructor === KENTICO_TYPES.HEADER) {
        try {
          this.modifiedStyles = styleFactory(JSON.parse(root.atob(model.htmlmodifiers)));
        } catch (err) {
          this.modifiedStyles = {};
          console.error('error when parsing html modifiers string to JSON by Parser: ', err);
        }

        switch (model.size) {
        case DOM_NODES.H2:
          return (
            <Header2
              style={this.modifiedStyles}
              dangerouslySetInnerHTML={{
                __html: safeHtmlWithBr(model.text),
              }}
            />
          );
        case DOM_NODES.H3:
          return (
            <Header3
              style={this.modifiedStyles}
              dangerouslySetInnerHTML={{
                __html: safeHtmlWithBr(model.text),
              }}
            />
          );
        case DOM_NODES.H4:
          return (
            <Header4
              style={this.modifiedStyles}
              dangerouslySetInnerHTML={{
                __html: safeHtmlWithBr(model.text),
              }}
            />
          );
        case DOM_NODES.H5:
          return (
            <Header5
              style={this.modifiedStyles}
              dangerouslySetInnerHTML={{
                __html: safeHtmlWithBr(model.text),
              }}
            />
          );
        default:
          return <span style={this.modifiedStyles}> {/* Element not found */} </span>;
        }
      } else if (model.constructor === KENTICO_TYPES.DIVIDED_BLOG_SECTION) {
        return <DividedBlogSection data={model} />;
      } else if (model.constructor === KENTICO_TYPES.BLOG_IMAGE_SLIDER) {
        const slides = (JSON.parse(model.slides));
        return <BlogImageSlider slides={slides} />;
      }
    }
    if (domNode.name === DOM_NODES.P) {
      if (this.cameFromBlogArticle) {
        return (
          <Paragraph blogArticle>
            {domNode.children.map((paragraphChild, index) => {
              if (paragraphChild.name === DOM_NODES.STRONG) {
                return (
                  <BlogArticleBoldText
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: paragraphChild.children[0].data,
                    }}
                  />
                );
              }
              if (paragraphChild.name === DOM_NODES.EM) {
                return (
                  <ItalicText
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: paragraphChild.children[0].data,
                    }}
                  />
                );
              }
              if (paragraphChild.name === DOM_NODES.A) {
                return (
                  <BlogArticleLink
                    {...paragraphChild.attribs}
                  >
                    {paragraphChild && paragraphChild.children.map(this.parseFunction)}
                  </BlogArticleLink>
                );
              }
              return paragraphChild.data;
            })}
          </Paragraph>
        );
      } return (
        <Paragraph>
          {domNode.children.map((paragraphChild, index) => {
            if (paragraphChild.name === DOM_NODES.STRONG) {
              return (
                <BoldText
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: paragraphChild.children[0].data,
                  }}
                />
              );
            }
            if (paragraphChild.name === DOM_NODES.EM) {
              return (
                <ItalicText
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: paragraphChild.children[0].data,
                  }}
                />
              );
            }
            return paragraphChild.data;
          })}
        </Paragraph>
      );
    }

    if (domNode.name === DOM_NODES.IMG) {
      const model = domNode.attribs;
      return <Image src={this.windowStore.adjustImageSize(model.src)} alt={model.alt} />;
    }

    if (domNode.name === DOM_NODES.FIGURE && domNode.children.length > 0
      && domNode.children[0].name === DOM_NODES.IMG) {
      const model = domNode.children[0].attribs;
      return (
        <Image
          src={this.windowStore.adjustImageSize(model.src)}
          alt={model.alt}
          style={this.modifiedStyles}
        />
      );
    }
    return domNode.data;
  };

  parserOptions = {
    replace: this.parseFunction,
  };

  render() {
    const { htmlBody } = this.props;
    return htmlBody ? Parser(htmlBody.resolveHtml(), this.parserOptions) : null;
  }
}



// WEBPACK FOOTER //
// ./src/blocks/Parser/Parser.jsx